/**
 * @author: lv.chen
 * @date: 2025/4/30
 * @desc: 编辑Technical组件
 */
import React, { useState, useEffect } from 'react';
// import classnames from 'classnames';
import { cloneDeep } from 'lodash-es';
import { Button, Modal } from 'tea-component';
import DynamicForm from '@gp/components/DynamicForm';
import useRequest from '@gp/hooks/useRequest';
import { fetchProjectTechnicalsConditions, editProjectTechnicals } from '@gp/services/projectTechnical';
import { isEmpty, reportBtnOperationLog } from '@gp/utils/common';
import { initializeTechnicalModuleValues } from '../../utils';
import { formatTechnicalsConditions, formatTechnicalsEditParams } from './utils';
import { CONSTANT_TEXTS, FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import { ProjectTechnicalModule } from '@gp/enums/project';
import { FIELDS, CONTROLS, VALIDATORS, VALIDATIONS } from './dynamicFormCfg';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import type { IBaseProps } from '@home/types/common';
import type { IDynamicFormField, IDynamicFormFieldValidation } from '@gp/components/DynamicForm/types';
import type { ICustomizeTagSelectValue } from '@gp/components/CustomizeTagSelect/types';
import type { IDynamicBaseFormControl } from '@gp/components/DynamicForm/components/FormControl/types';
import type { IPipelineProjectTechnicalFormValue } from './types';
import './index.scss';

interface IEditTechnicalProps extends IBaseProps {
  projectId?: string;
  modules?: IPipelineProjectTechnicalModule[] | null;
  onEditSuccess?: () => void;
}

const MODAL_TITLE = `${CONSTANT_TEXTS.EDIT} Technical Framework`;

const DEFAULT_FORM_VALUE: IPipelineProjectTechnicalFormValue = initializeTechnicalModuleValues({
  checked: [],
  options: [],
});

const ALL_ERRORS: Record<ProjectTechnicalModule, string> = initializeTechnicalModuleValues(FIELD_COMMON_ERR_MSG);

const getListErrors = (key: string, value?: any[]): Record<string, string> => {
  if (!Array.isArray(value) || !value.length) return { [key]: FIELD_COMMON_ERR_MSG };
  const filteredValue = value.filter((v) => !isEmpty(v));
  return filteredValue.length ? {} : { [key]: FIELD_COMMON_ERR_MSG };
};

const getErrors = (values?: IPipelineProjectTechnicalFormValue): Record<string, string> => {
  if (!values) return ALL_ERRORS;
  const errors: Record<string, string> = {};
  Object.entries(values).forEach(([key, value]) => {
    const { required, validator } = VALIDATORS[key as unknown as ProjectTechnicalModule] || {};
    if (typeof validator === 'function') {
      const { status, message } = validator(value, values);
      if (status === 'error') {
        Object.assign(errors, { [key]: message });
      }
    } else if (required) {
      if (Array.isArray(value)) {
        Object.assign(errors, getListErrors(key, value));
      } else if (isEmpty(value)) {
        Object.assign(errors, { [key]: FIELD_COMMON_ERR_MSG });
      }
    }
  });
  return errors;
};

const getValidations = (
  values: IPipelineProjectTechnicalFormValue,
): Record<ProjectTechnicalModule, IDynamicFormFieldValidation> => {
  const errors = getErrors(values);
  const validationsTmp = cloneDeep(VALIDATIONS);
  Object.keys(validationsTmp).forEach((key) => {
    const error = errors?.[key];
    if (validationsTmp[key as unknown as ProjectTechnicalModule] && error) {
      validationsTmp[key as unknown as ProjectTechnicalModule].status = 'error';
      validationsTmp[key as unknown as ProjectTechnicalModule].message = error;
    }
  });
  return validationsTmp;
};

const EditTechnical: React.FC<IEditTechnicalProps> = (props) => {
  const { projectId, modules, onEditSuccess } = props;
  const {
    loading: technicalsConditionsResLoading,
    data: technicalsConditionsRes,
    run: technicalsConditionsResRun,
  } = useRequest(fetchProjectTechnicalsConditions, { manual: true });

  const technicalsConditions = formatTechnicalsConditions(technicalsConditionsRes);

  const [visible, setVisible] = useState(false);

  // 是否点击过confirm按钮进行提交表单
  const [submitted, setSubmitted] = useState(false);

  // 表单字段集合
  const [fields] = useState<IDynamicFormField[]>(FIELDS);

  // 表单控件映射，键为表单字段key
  const [controls, setControls] = useState<Record<ProjectTechnicalModule, IDynamicBaseFormControl>>(CONTROLS);

  // 表单校验结果信息映射，键为表单字段key
  const [validations, setValidations] =
    useState<Record<ProjectTechnicalModule, IDynamicFormFieldValidation>>(VALIDATIONS);

  const [value, setValue] = useState<Record<ProjectTechnicalModule, ICustomizeTagSelectValue>>(DEFAULT_FORM_VALUE);

  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [initialParams, setInitialParams] = useState(DEFAULT_FORM_VALUE);

  const handleValidate = (
    payload: IPipelineProjectTechnicalFormValue,
    submitted: boolean,
  ): Record<string, IDynamicFormFieldValidation> => {
    if (!submitted) return validations;
    const validationsTmp = getValidations(payload);
    setValidations(validationsTmp);
    return validationsTmp;
  };

  const handleChange = (payload: IPipelineProjectTechnicalFormValue) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
    handleValidate(valueTmp, submitted);
  };

  const handleSubmit = async () => {
    const validations = handleValidate(value, true);
    const isValid = Object.values(validations).every((validation) => validation?.status === 'success');
    setSubmitted(true);
    if (isValid) {
      const params = formatTechnicalsEditParams(value);
      try {
        await editProjectTechnicals({ ...params, project_id: projectId || '' });
        setVisible(false);
        onEditSuccess?.();
        deleteSaveParamsByPageKey(pageKeyCur);
      } catch (exception) {
        console.warn(exception);
      } finally {
        reportBtnOperationLog('PIPELINE_DETAIL_TECHNICAL_EDIT_SUBMIT');
      }
    }
  };

  const initDataFun = async () => {
    try {
      await technicalsConditionsResRun({ id: projectId });
      if (showRestorePrompt(pageKeyCur)) {
        setAutoSaveVisible(true);
      } else {
        if (!visible) {
          setVisible(true);
        }
      }
    } catch (exception) {
    } finally {
    }
  };

  const resetValue = () => {
    setSubmitted(false);
    setValidations(VALIDATIONS);
    setValue(DEFAULT_FORM_VALUE);
  };

  useEffect(() => {
    if (technicalsConditionsResLoading) return;
    const controlsTmp = initializeTechnicalModuleValues<any>({}, (key) => {
      return { ...controls?.[key], loading: false, options: technicalsConditions?.[key] || [] };
    });
    setControls(controlsTmp);

    const valueTmp = initializeTechnicalModuleValues<any>({ checked: [], options: [] }, (key) => {
      const module = modules?.find((item) => item?.module === key);
      const { list } = module || {};
      const moduleValueTmp = list?.map((item) => item?.id);
      return { ...value?.[key], checked: moduleValueTmp || [], options: technicalsConditions?.[key] || [] };
    });
    setValue(valueTmp);
    setInitialParams(valueTmp);

    return () => {
      const controlsTmp = initializeTechnicalModuleValues<any>({}, (key) => {
        return { ...controls?.[key], loading: true, options: [] };
      });
      setControls(controlsTmp);

      const valueTmp = initializeTechnicalModuleValues<any>({ checked: [], options: [] });
      setValue(valueTmp);
      setInitialParams(valueTmp);
    };
  }, [technicalsConditionsResLoading]);

  useEffect(() => {
    if (!projectId) return;
    if (!visible) return;
    // technicalsConditionsResRun({ id: projectId });
  }, [projectId, visible]);

  const pageKeyCur = `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_tech_edit}_${projectId}`;

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams,
    saveParamsCur: value,
    isBegin: visible,
    isChangeValue: true,
  });

  const handleAutoSaveConfirm = () => {
    const newValue = getSaveParamsValue(pageKeyCur) || {};
    setValue(newValue);
    setAutoSaveVisible(false);
    if (!visible) {
      setVisible(true);
    }
  };

  const handleAutoSaveCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setAutoSaveVisible(false);
    if (!visible) {
      setVisible(true);
    }
  };

  return (
    <>
      <Button
        type="link"
        onClick={(e) => {
          e?.stopPropagation();
          initDataFun();
        }}
      >
        {CONSTANT_TEXTS.EDIT}
      </Button>
      <Modal
        className="dbg-pipeline-project-details-technical-edit-modal"
        caption={MODAL_TITLE}
        size={1020}
        visible={visible}
        onClose={() => {
          deleteSaveParamsByPageKey(pageKeyCur);
          setVisible(false);
        }}
      >
        <Modal.Body>
          <DynamicForm
            formProps={{
              className: 'dbg-pipeline-project-details-technical-edit-form',
              layout: 'fixed',
              fixedLabelWidth: 168,
            }}
            formItemProps={{
              className: 'dbg-pipeline-project-details-technical-edit-form-item',
              showStatusIcon: false,
            }}
            fields={fields}
            controls={controls}
            validations={validations}
            value={value}
            onChange={handleChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            disabled={technicalsConditionsResLoading}
            onClick={(e) => {
              e?.stopPropagation();
              handleSubmit();
            }}
          >
            Confirm
          </Button>
          <Button
            onClick={(e) => {
              e?.stopPropagation();
              deleteSaveParamsByPageKey(pageKeyCur);
              resetValue();
              setVisible(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal visible={autoSaveVisible} onConfirm={handleAutoSaveConfirm} onClose={handleAutoSaveCancel} />
    </>
  );
};

export default EditTechnical;
